import type { TFunction } from 'i18next';
import { z } from 'zod';
import type { GetStationChainResponse } from '@/api/generated/schemas';
import { DEFAULT_STATION_CHAIN_COLOR } from './stationChainColors';

/** Nástupní místo nabízené u zastávky (výběr `stationDetailId`). */
const stopPointSchema = z.object({
  id: z.number(),
  name: z.string(),
  lat: z.number().nullable(),
  lng: z.number().nullable(),
});

/** Stabilní lokální klíč zastávky (jen UI — pro React seznam a drag). */
export function newStationChainStopKey(): string {
  return `new-${crypto.randomUUID()}`;
}

function buildStopSchema() {
  return z.object({
    key: z.string(),
    id: z.string().nullable(),
    stationId: z.number(),
    stationName: z.string(),
    stationCode: z.string().nullable(),
    stationIsDomestic: z.boolean(),
    stationLat: z.number().nullable(),
    stationLng: z.number().nullable(),
    stationDetailId: z.number().nullable(),
    points: z.array(stopPointSchema),
    dwell: z.number().min(0),
    travel: z.number().min(0),
    travelAuto: z.boolean(),
  });
}

/**
 * Factory Zod schématu detailu trasy. Lokalizované hlášky se vkládají přímo do schématu, proto se
 * staví z `t` až za běhu. Typ hodnot se odvozuje ze schématu (`StationChainFormValues`).
 */
export function buildStationChainFormSchema(t: TFunction) {
  return z.object({
    name: z.string().trim().min(1, t('stationChain.validation.nameRequired')),
    code: z.string(),
    color: z.string().min(1),
    status: z.enum(['Active', 'Draft', 'Archived']),
    stops: z.array(buildStopSchema()),
  });
}

export type StationChainFormValues = z.infer<ReturnType<typeof buildStationChainFormSchema>>;
export type StationChainStopValue = StationChainFormValues['stops'][number];

/** Prázdné výchozí hodnoty pro režim zakládání (Koncept). */
export function emptyStationChainFormValues(): StationChainFormValues {
  return {
    name: '',
    code: '',
    color: DEFAULT_STATION_CHAIN_COLOR,
    status: 'Draft',
    stops: [],
  };
}

/** Mapuje detail trasy z API na hodnoty formuláře (režim editace). */
export function fromGetStationChainResponse(
  response: GetStationChainResponse
): StationChainFormValues {
  return {
    name: response.name,
    code: response.code ?? '',
    color: response.color,
    status: response.status,
    stops: response.stops.map((stop) => ({
      key: `srv-${stop.id}`,
      id: stop.id,
      stationId: stop.stationId,
      stationName: stop.stationName,
      stationCode: stop.stationCode ?? null,
      stationIsDomestic: stop.stationIsDomestic,
      stationLat: stop.stationLatitude ?? null,
      stationLng: stop.stationLongitude ?? null,
      stationDetailId: stop.stationDetailId ?? null,
      points: stop.stationDetails.map((point) => ({
        id: point.id,
        name: point.name,
        lat: point.latitude ?? null,
        lng: point.longitude ?? null,
      })),
      dwell: stop.dwell,
      travel: stop.travel,
      travelAuto: stop.travelAuto,
    })),
  };
}
